import { baseApi } from "./baseApi";
import {
  ServiceMonthlyCatalogItem,
  ServiceMonthlyCatalogResponse,
  ServiceMonthlyDeleteResponse,
  ServiceMonthlyIncome,
  ServiceMonthlyIncomeListResponse,
  ServiceMonthlyIncomePayload,
  ServiceMonthlyIncomeResponse,
  ServiceMonthlyOutcome,
  ServiceMonthlyOutcomeListResponse,
  ServiceMonthlyOutcomePayload,
  ServiceMonthlyOutcomeResponse,
} from "@interfaces/serviceMonthly";

/**
 * API "Perfil de ingresos y egresos" (servicio cuentas, `/v1/cuentas`). Guarda la pantalla
 * `PerfilIngresosMercader`: ingresos por servicio, egresos por concepto y, aparte, la declaración bajo
 * protesta (con `patchCuenta`, campo `income_declaration_accepted`). Totales, utilidad y margen los calcula
 * el front. Las listas aceptan `page`/`size`; con el default (100) una sola llamada trae todo.
 */
const serviceMonthlyApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // ─── Catálogos ───────────────────────────────────────────────────────────
    /** Servicios que generan ingreso (opciones del selector), activos y ordenados por `order`. */
    getServiceMonthlyIncomeCatalog: build.query<ServiceMonthlyCatalogItem[], { segment: string }>({
      query: ({ segment }) => `cuentas/catalogs/service_monthly_income?segment=${segment}`,
      transformResponse: (response: ServiceMonthlyCatalogResponse) => response.data.data,
    }),
    /** Conceptos de egreso mensual; `description` es el texto de ayuda de cada renglón. */
    getServiceMonthlyOutcomeCatalog: build.query<ServiceMonthlyCatalogItem[], { segment: string }>({
      query: ({ segment }) => `cuentas/catalogs/service_monthly_outcome?segment=${segment}`,
      transformResponse: (response: ServiceMonthlyCatalogResponse) => response.data.data,
    }),

    // ─── Ingresos por servicio ───────────────────────────────────────────────
    /** Ingresos activos de la cuenta, en el orden en que se capturaron. */
    getServiceMonthlyIncomeList: build.query<ServiceMonthlyIncome[], { clientDetailsId: string }>({
      query: ({ clientDetailsId }) => `cuentas/${clientDetailsId}/service_monthly_income`,
      transformResponse: (response: ServiceMonthlyIncomeListResponse) => response.data.data,
    }),
    /** Crea un bloque de ingreso; el `id` que regresa sirve para editarlo o borrarlo después. */
    postServiceMonthlyIncome: build.mutation<
      ServiceMonthlyIncome,
      { clientDetailsId: string; body: ServiceMonthlyIncomePayload }
    >({
      query: ({ clientDetailsId, body }) => ({
        url: `cuentas/${clientDetailsId}/service_monthly_income`,
        method: "POST",
        body,
      }),
      transformResponse: (response: ServiceMonthlyIncomeResponse) => response.data,
    }),
    /** Actualiza un bloque: solo los campos que cambian. Las reglas se validan contra el registro completo. */
    patchServiceMonthlyIncome: build.mutation<
      ServiceMonthlyIncome,
      { clientDetailsId: string; id: string; body: Partial<ServiceMonthlyIncomePayload> }
    >({
      query: ({ clientDetailsId, id, body }) => ({
        url: `cuentas/${clientDetailsId}/service_monthly_income/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: ServiceMonthlyIncomeResponse) => response.data,
    }),
    /** Borrado lógico de un bloque (ícono de bote de basura). */
    deleteServiceMonthlyIncome: build.mutation<{ id: string }, { clientDetailsId: string; id: string }>({
      query: ({ clientDetailsId, id }) => ({
        url: `cuentas/${clientDetailsId}/service_monthly_income/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ServiceMonthlyDeleteResponse) => response.data,
    }),

    // ─── Egresos mensuales ───────────────────────────────────────────────────
    /** Egresos activos de la cuenta; se cruzan por `service_monthly_outcome_catalog_id` con el catálogo. */
    getServiceMonthlyOutcomeList: build.query<ServiceMonthlyOutcome[], { clientDetailsId: string }>({
      query: ({ clientDetailsId }) => `cuentas/${clientDetailsId}/service_monthly_outcome`,
      transformResponse: (response: ServiceMonthlyOutcomeListResponse) => response.data.data,
    }),
    /**
     * Crea el egreso de un concepto. Si el concepto ya estaba capturado responde 400 con el `id` existente
     * en `data.id` ("Registro previo del concepto de egreso"): en ese caso hacer PATCH a ese id.
     */
    postServiceMonthlyOutcome: build.mutation<
      ServiceMonthlyOutcome,
      { clientDetailsId: string; body: ServiceMonthlyOutcomePayload }
    >({
      query: ({ clientDetailsId, body }) => ({
        url: `cuentas/${clientDetailsId}/service_monthly_outcome`,
        method: "POST",
        body,
      }),
      transformResponse: (response: ServiceMonthlyOutcomeResponse) => response.data,
    }),
    /** Actualiza el monto (o el concepto, si el nuevo no está capturado ya). */
    patchServiceMonthlyOutcome: build.mutation<
      ServiceMonthlyOutcome,
      { clientDetailsId: string; id: string; body: Partial<ServiceMonthlyOutcomePayload> }
    >({
      query: ({ clientDetailsId, id, body }) => ({
        url: `cuentas/${clientDetailsId}/service_monthly_outcome/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: ServiceMonthlyOutcomeResponse) => response.data,
    }),
    /** Borrado lógico del egreso; después se puede volver a crear el mismo concepto con POST. */
    deleteServiceMonthlyOutcome: build.mutation<{ id: string }, { clientDetailsId: string; id: string }>({
      query: ({ clientDetailsId, id }) => ({
        url: `cuentas/${clientDetailsId}/service_monthly_outcome/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ServiceMonthlyDeleteResponse) => response.data,
    }),
  }),
});

export const {
  useLazyGetServiceMonthlyIncomeCatalogQuery,
  useLazyGetServiceMonthlyOutcomeCatalogQuery,
  useLazyGetServiceMonthlyIncomeListQuery,
  usePostServiceMonthlyIncomeMutation,
  usePatchServiceMonthlyIncomeMutation,
  useDeleteServiceMonthlyIncomeMutation,
  useLazyGetServiceMonthlyOutcomeListQuery,
  usePostServiceMonthlyOutcomeMutation,
  usePatchServiceMonthlyOutcomeMutation,
  useDeleteServiceMonthlyOutcomeMutation,
} = serviceMonthlyApi;
